<template>
	<div class="container mx-auto flex max-w-5xl px-4">
		<div class="flex flex-1 flex-col justify-between">
			<div class="flex p-2">
				<HelpfulTips :text="'Why we ask'">
					Our quotes are not automated. We will carefully research your car to make a
					competitive quote. We ask for your info so we can send you a quote for your
					{{ form.make }} once it is ready. We will only contact you about your car. Opt
					out at any time.
				</HelpfulTips>

				<div class="-mt-5 grow basis-1/2 lg:mt-0">
					<form class="flex flex-col" action="#">
						<AppInput
							ref="nameInputRef"
							v-model="form.name"
							class="mb-4"
							type="text"
							name="name"
							placeholder="Name"
							:rules="[
								() =>
									form[Validations.IsNameValid] ||
									(isFullNameRequired
										? 'Please enter your full name'
										: 'Please enter your name'),
							]"
							@focus="eventBus.emit({ name: EVENT_BUS_EVENTS.COLLAPSE_WHY_WE_ASK })"
							@submit="eventBus.emit({ name: EVENT_BUS_EVENTS.FORM_NEXT_STEP })"
						>
							<template #prepend>
								<PersonSvg class="h-4 w-4" />
							</template>
						</AppInput>

						<AppInput
							v-model="form.email"
							class="mb-4"
							type="text"
							name="email"
							placeholder="Email"
							:rules="[
								() =>
									form[Validations.IsEmailValid] || 'Please enter a valid email',
							]"
							@focus="eventBus.emit({ name: EVENT_BUS_EVENTS.COLLAPSE_WHY_WE_ASK })"
							@submit="eventBus.emit({ name: EVENT_BUS_EVENTS.FORM_NEXT_STEP })"
						>
							<template #prepend>
								<EmailSvg class="h-4 w-4" />
							</template>
						</AppInput>

						<AppInput
							v-model="form.phone"
							v-maska="phoneMask"
							class="mb-4"
							type="phone"
							name="phone"
							placeholder="Phone"
							inputmode="numeric"
							is-phone
							:rules="[
								() =>
									form[Validations.IsPhoneValid] ||
									'Please enter a valid phone number',
							]"
							@focus="eventBus.emit({ name: EVENT_BUS_EVENTS.COLLAPSE_WHY_WE_ASK })"
							@submit="eventBus.emit({ name: EVENT_BUS_EVENTS.FORM_NEXT_STEP })"
						>
							<template #prepend>
								<PhoneSvg class="h-4 w-4" />
							</template>
						</AppInput>

						<AppInput
							v-if="getConfigContent('zipRequired', false)"
							v-model="form.zip"
							v-maska="zipMask"
							class="mb-4"
							type="ZIP"
							name="ZIP"
							placeholder="ZIP"
							inputmode="numeric"
							:rules="[
								() => form[Validations.IsZIPValid] || 'Please enter a valid ZIP',
							]"
							@focus="eventBus.emit({ name: EVENT_BUS_EVENTS.COLLAPSE_WHY_WE_ASK })"
							@submit="eventBus.emit({ name: EVENT_BUS_EVENTS.FORM_NEXT_STEP })"
						>
							<template #prepend>
								<MarkerSvg class="h-5 w-5" />
							</template>
						</AppInput>

						<div v-if="errorMessage" class="xs:mx-8 text-error mx-6">
							{{ errorMessage }}
						</div>
					</form>
				</div>
			</div>

			<p v-if="isContactInfoValid" class="text-12 p-2 pb-0 opacity-60">
				{{ disclosureText }} By clicking "Get my offer", you also agree to our
				<a
					class="text-main-400"
					target="_blank"
					href="https://www.driveo.com/terms-conditions/"
				>
					Terms
				</a>
				and
				<a
					class="text-main-400"
					target="_blank"
					href="https://www.driveo.com/privacy-policy/"
				>
					Privacy Policy.
				</a>
			</p>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { useFormStore, Validations } from '../../../stores/form'
	import { phoneMask, zipMask } from '../../../services/helpers'
	import PhoneSvg from '../components/icons/PhoneSvg.vue'
	import EmailSvg from '../components/icons/EmailSvg.vue'
	import PersonSvg from '../components/icons/PersonSvg.vue'
	import AppInput from '../../common/AppInput.vue'
	import { computed, ref } from 'vue'
	import HelpfulTips from '../components/HelpfulTips.vue'
	import { EVENT_BUS_EVENTS, useEventBus } from '../../../stores/eventBus'
	import { useConfigStore } from '../../../stores/config'
	import MarkerSvg from '../components/icons/MarkerSvg.vue'

	// hooks
	const form = useFormStore()
	const { config, getConfigContent } = useConfigStore()
	const eventBus = useEventBus()

	// refs
	const nameInputRef = ref()
	const errorMessage = ref('')

	// computed
	const isContactInfoValid = computed(() => form[Validations.IsContactInfoValid])
	const isFullNameRequired = computed(() => getConfigContent('fullName', false))

	const disclosureText = computed(() => {
		const dealerName = config.location?.dealership_name || 'its associated Dealers'

		return config.location?.state === 'OK' || config.location?.state === 'FL'
			? `I hereby agree and authorize Driveo and ${dealerName} to call or text me for sales purposes at the telephone number I provided using an automated system for the selection or dialing of telephone numbers. I know that I am not required to agree as a condition of purchasing any property, goods, or services. I further understand that, by clicking the "Get my offer" button below, I am submitting my electronic signature.`
			: `By clicking "Get my offer", you consent to receive text messages from Driveo and/or ${dealerName}, regarding the quote for your car, including possible automated communications. This consent isn't a condition of a sale, but it overrides any DNC registration.`
	})

	eventBus.$onAction(({ args }) => {
		if (args[0].name === EVENT_BUS_EVENTS.SUBMIT_FORM_ERROR)
			errorMessage.value = args[0].data as string
	})
</script>
